import type { StripeTokenAccount } from '@features/booking/state/types';

const GUARANTEE_TYPE_DEPOSIT = 'deposit';
const GUARANTEE_TYPE_NO_SHOW = 'no_show';

/**
 * Which Amex operations the backend routes to the PLATFORM Stripe account for this
 * venue (`restaurant.amexPlatformRouting`, computed server-side from the venue's Stripe
 * setup + its `enable_amex_platform_routing` flag). The backend accepts platform tokens
 * by that same table for OWN-KEY venues; for the rest (marketplace / shared key) it
 * publishes `off` but the platform key IS their key, so a legacy no-show switch there is
 * a no-op the backend keeps accepting.
 *
 *   all           → Amex deposits AND no-show guarantees tokenize on the platform key
 *   anything else → the pre-WS2 legacy `allowAMEXBR` rule, unchanged:
 *                   false ≡ no_show_only (no-show on the platform, deposit blocked)
 *                   true  ≡ off          (every card on the venue's own key)
 *
 * Only `all` is read from the backend. `no_show_only` is what the backend derives from
 * `allowAMEXBR = false` anyway, and a venue whose backend mode is `off` while its legacy
 * bit is false (a shared-key venue) must keep behaving exactly as today — so the legacy
 * bit, not the mode, decides everything below `all`. An absent or unrecognized value
 * (older backend, a mode this bundle predates) therefore also lands on the legacy rule,
 * never on "unblocked".
 */
export type AmexPlatformRouting = 'all' | 'no_show_only' | 'off';
export const AMEX_PLATFORM_ROUTING_ALL: AmexPlatformRouting = 'all';
export const AMEX_PLATFORM_ROUTING_NO_SHOW_ONLY: AmexPlatformRouting = 'no_show_only';
export const AMEX_PLATFORM_ROUTING_OFF: AmexPlatformRouting = 'off';

export type AmexRoutingVenue = {
  amexPlatformRouting?: AmexPlatformRouting | null;
  allowAMEXBR: boolean | undefined;
};

export type AmexChangeInput = AmexRoutingVenue & {
  cardBrand: string | null | undefined;
  guaranteeType: string | null | undefined;
  tokenCreatedWithPlatformKey: boolean;
};

export type StripeTokenAccountInput = AmexRoutingVenue & {
  cardBrand: string | null | undefined;
  guaranteeType: string | null | undefined;
};

export function isAmexBrand(cardBrand: string | null | undefined): boolean {
  if (!cardBrand) return false;
  const normalizedBrand = cardBrand.toLowerCase();
  return normalizedBrand === 'amex' || normalizedBrand === 'american express';
}

export function resolveAmexPlatformRouting({
  amexPlatformRouting,
  allowAMEXBR,
}: AmexRoutingVenue): AmexPlatformRouting {
  if (amexPlatformRouting === AMEX_PLATFORM_ROUTING_ALL) return AMEX_PLATFORM_ROUTING_ALL;
  return allowAMEXBR ? AMEX_PLATFORM_ROUTING_OFF : AMEX_PLATFORM_ROUTING_NO_SHOW_ONLY;
}

/**
 * The MercadoPago forms' Amex deposit block. MP has no platform token to route to, so
 * the backend mode never applies there: the legacy bit alone decides, whatever
 * `amexPlatformRouting` says (MP venues receive `off`; this must hold even if that changed).
 * `isAmex` comes from the BIN (34/37) because the MP inputs hold the raw number.
 */
export function isAmexDepositBlockedForMercadoPago({
  isAmex,
  allowAMEXBR,
  guaranteeType,
}: {
  isAmex: boolean;
  allowAMEXBR: boolean | undefined;
  guaranteeType: string | null | undefined;
}): boolean {
  return isAmex && !allowAMEXBR && guaranteeType === GUARANTEE_TYPE_DEPOSIT;
}

export function shouldUsePlatformStripeToken({
  cardBrand,
  amexPlatformRouting,
  allowAMEXBR,
  guaranteeType,
}: StripeTokenAccountInput): boolean {
  if (!isAmexBrand(cardBrand)) return false;
  const routing = resolveAmexPlatformRouting({ amexPlatformRouting, allowAMEXBR });
  return (
    routing === AMEX_PLATFORM_ROUTING_ALL ||
    (routing === AMEX_PLATFORM_ROUTING_NO_SHOW_ONLY && guaranteeType === GUARANTEE_TYPE_NO_SHOW)
  );
}

/**
 * The legacy "Stripe BR cannot capture Amex" block on deposits. Lifted only when the
 * backend routes Amex deposits to the platform (`all`); otherwise exactly the pre-WS2
 * rule (blocked when the venue does not allow Amex BR).
 */
export function isAmexDepositBlocked({
  cardBrand,
  amexPlatformRouting,
  allowAMEXBR,
  guaranteeType,
}: AmexRoutingVenue & { cardBrand: string | null | undefined; guaranteeType: string | null | undefined }): boolean {
  if (!isAmexBrand(cardBrand) || guaranteeType !== GUARANTEE_TYPE_DEPOSIT) return false;
  return resolveAmexPlatformRouting({ amexPlatformRouting, allowAMEXBR }) === AMEX_PLATFORM_ROUTING_NO_SHOW_ONLY;
}

export function getStripeTokenAccount(input: StripeTokenAccountInput): StripeTokenAccount {
  return shouldUsePlatformStripeToken(input) ? 'platform' : 'restaurant';
}

export function getStripePublishableKeyForTokenAccount({
  tokenAccount,
  restaurantPublishableKey,
  platformPublishableKey,
}: {
  tokenAccount: StripeTokenAccount | null | undefined;
  restaurantPublishableKey: string;
  platformPublishableKey: string;
}): string {
  return tokenAccount === 'platform' ? platformPublishableKey : restaurantPublishableKey;
}

// Legacy used `guaranteeType !== 'deposit'`; shouldUsePlatformStripeToken
// uses `=== 'no_show'`. Equivalent for current values ('deposit' | 'no_show'),
// but callers must only set tokenCreatedWithPlatformKey once the reservation
// has loaded and guaranteeType is known.
export function computeAmexChange({
  cardBrand,
  amexPlatformRouting,
  allowAMEXBR,
  guaranteeType,
  tokenCreatedWithPlatformKey,
}: AmexChangeInput): boolean {
  const semanticAmexChange =
    shouldUsePlatformStripeToken({ cardBrand, amexPlatformRouting, allowAMEXBR, guaranteeType });

  return semanticAmexChange && tokenCreatedWithPlatformKey;
}
